import { useEffect, useRef, useState } from "react";
import { Search, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import {
  searchUSDA,
  searchSwiss,
  listCustomIngredients,
  deleteCustomIngredient,
} from "../lib/api";
import { titleCase } from "../lib/nutrition";
import { Skeleton } from "./ui/skeleton";

const SOURCE_LABEL = {
  BLV: { flag: "🇨🇭", label: "BLV · Naehrwertdaten.ch" },
  USDA: { flag: "🇺🇸", label: "USDA · FoodData Central" },
};

export const IngredientSearch = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("BLV"); // "BLV" (FR) | "USDA" (EN)
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [library, setLibrary] = useState([]);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Load personal ingredient library once
  useEffect(() => {
    listCustomIngredients()
      .then((data) => setLibrary(data || []))
      .catch(() => setLibrary([]));
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data =
          source === "BLV"
            ? await searchSwiss(query.trim(), 12)
            : await searchUSDA(query.trim(), 12);
        setResults(data);
        setOpen(true);
      } catch (e) {
        setError(
          source === "BLV"
            ? "Erreur de recherche BLV (base suisse)."
            : "Erreur de recherche USDA."
        );
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, source]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (food) => {
    // Normalize food shape to the one expected by RecipeBuilder.addFromUSDA
    // (which forwards to newIngredientFromFood)
    if (source === "BLV") {
      onSelect({
        fdc_id: null,
        blv_dbid: food.blv_dbid,
        description: food.description,
        brand: null,
        data_type: "BLV",
        nutrients_per_100g: food.nutrients_per_100g,
        _source: "BLV",
        _name_fr: food.description, // Swiss names already in French
      });
    } else {
      onSelect({
        ...food,
        _source: "USDA",
      });
    }
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const handleSelectLibrary = (lib) => {
    onSelect({
      fdc_id: null,
      description: lib.name_fr,
      brand: null,
      data_type: "custom",
      nutrients_per_100g: lib.nutrients_per_100g,
      _custom: true,
      _name_fr: lib.name_fr,
    });
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const handleDeleteLibrary = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Retirer cet ingrédient de votre bibliothèque ?")) return;
    try {
      await deleteCustomIngredient(id);
      setLibrary((l) => l.filter((x) => x.id !== id));
    } catch {
      // silent
    }
  };

  const q = query.trim().toLowerCase();
  const filteredLibrary =
    q.length >= 2 ? library.filter((l) => l.name_fr.toLowerCase().includes(q)) : [];

  useEffect(() => {
    if (filteredLibrary.length > 0) setOpen(true);
  }, [filteredLibrary.length]);

  const isBLV = source === "BLV";
  const placeholder = isBLV
    ? "Rechercher en français (ex: poulet, riz cuit, lait entier)…"
    : "Rechercher en anglais (ex: chicken, oats, coconut milk)…";

  const resultKey = (food) =>
    isBLV ? `blv-${food.blv_dbid}` : `usda-${food.fdc_id}`;

  return (
    <div
      className="relative w-full"
      ref={boxRef}
      data-testid="ingredient-search"
    >
      {/* Source toggle */}
      <div
        className="flex items-center gap-1 mb-2 p-1 bg-stone-100 rounded-lg w-fit"
        data-testid="source-toggle"
      >
        <button
          type="button"
          onClick={() => setSource("BLV")}
          className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors flex items-center gap-1.5 ${
            isBLV
              ? "bg-white text-emerald-700 shadow-sm"
              : "text-stone-500 hover:text-stone-700"
          }`}
          data-testid="source-blv-btn"
          title="Base suisse OSAV/BLV (français)"
        >
          <span>🇨🇭</span>
          Suisse · FR
        </button>
        <button
          type="button"
          onClick={() => setSource("USDA")}
          className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-md transition-colors flex items-center gap-1.5 ${
            !isBLV
              ? "bg-white text-emerald-700 shadow-sm"
              : "text-stone-500 hover:text-stone-700"
          }`}
          data-testid="source-usda-btn"
          title="USDA FoodData Central (anglais)"
        >
          <span>🇺🇸</span>
          USDA · EN
        </button>
      </div>

      <Search className="absolute left-4 top-[3.25rem] -translate-y-1/2 text-stone-400 w-5 h-5 pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="w-full bg-white border-2 border-stone-200 rounded-xl pl-12 pr-12 py-4 text-base focus:border-emerald-600 focus:outline-none focus:ring-0 transition-colors shadow-sm placeholder:text-stone-400"
        data-testid="ingredient-search-input"
      />
      {loading && (
        <Loader2 className="absolute right-4 top-[3.25rem] -translate-y-1/2 text-emerald-700 w-5 h-5 animate-spin" />
      )}

      {open && (loading || results.length > 0 || filteredLibrary.length > 0 || error) && (
        <div className="absolute top-[calc(100%+0.25rem)] left-0 right-0 mt-2 bg-white border border-stone-200 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] max-h-96 overflow-y-auto scrollbar-thin z-40 animate-in fade-in slide-in-from-top-2 duration-200">
          {error && (
            <div className="p-4 text-sm text-red-600" data-testid="search-error">
              {error}
            </div>
          )}
          {filteredLibrary.length > 0 && (
            <>
              <div className="px-4 py-2 bg-amber-50/60 border-b border-amber-100 flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-800">
                <Sparkles className="h-3 w-3" />
                Bibliothèque personnelle
              </div>
              {filteredLibrary.map((lib) => (
                <div
                  key={lib.id}
                  className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-stone-100 transition-colors flex items-center justify-between gap-3 group cursor-pointer"
                  onClick={() => handleSelectLibrary(lib)}
                  data-testid={`library-result-${lib.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-stone-900 truncate">{lib.name_fr}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      <span className="font-medium text-emerald-700">
                        {lib.nutrients_per_100g.calories} kcal
                      </span>
                      <span className="mx-1.5">·</span>
                      P {lib.nutrients_per_100g.protein}g · G{" "}
                      {lib.nutrients_per_100g.carbs}g · L {lib.nutrients_per_100g.fat}g
                      <span className="text-stone-400 ml-1">/ 100g</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteLibrary(e, lib.id)}
                    className="shrink-0 h-7 w-7 rounded-full text-stone-300 hover:text-red-600 hover:bg-red-50 grid place-items-center transition-colors"
                    title="Retirer de la bibliothèque"
                    data-testid={`library-delete-${lib.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  <span className="shrink-0 h-8 w-8 rounded-full bg-stone-100 group-hover:bg-amber-600 group-hover:text-white text-stone-500 grid place-items-center transition-colors">
                    <Plus className="h-4 w-4" />
                  </span>
                </div>
              ))}
              {results.length > 0 && (
                <div className="px-4 py-2 bg-stone-50/60 border-b border-stone-100 flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-stone-500">
                  <span>{SOURCE_LABEL[source].flag}</span>
                  {SOURCE_LABEL[source].label}
                </div>
              )}
            </>
          )}
          {loading && results.length === 0 && (
            <div className="p-3 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full bg-stone-100" />
              ))}
            </div>
          )}
          {results.map((food) => (
            <button
              type="button"
              key={resultKey(food)}
              onClick={() => handleSelect(food)}
              className="w-full text-left px-4 py-3 hover:bg-emerald-50 border-b border-stone-100 last:border-0 transition-colors flex items-center justify-between gap-3 group"
              data-testid={`search-result-${resultKey(food)}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-900 truncate">
                  {isBLV ? food.description : titleCase(food.description)}
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
                  {isBLV ? (
                    food.category && (
                      <span className="uppercase tracking-wider">{food.category}</span>
                    )
                  ) : (
                    <>
                      {food.brand && (
                        <span className="truncate max-w-[140px]">{titleCase(food.brand)}</span>
                      )}
                      {food.brand && <span>·</span>}
                      <span className="uppercase tracking-wider">{food.data_type}</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  <span className="font-medium text-emerald-700">
                    {food.nutrients_per_100g.calories} kcal
                  </span>
                  <span className="mx-1.5">·</span>
                  P {food.nutrients_per_100g.protein}g · G{" "}
                  {food.nutrients_per_100g.carbs}g · L {food.nutrients_per_100g.fat}g
                  <span className="text-stone-400 ml-1">/ 100g</span>
                </div>
              </div>
              <span className="shrink-0 h-8 w-8 rounded-full bg-stone-100 group-hover:bg-emerald-600 group-hover:text-white text-stone-500 grid place-items-center transition-colors">
                <Plus className="h-4 w-4" />
              </span>
            </button>
          ))}
          {!loading &&
            results.length === 0 &&
            filteredLibrary.length === 0 &&
            !error &&
            query.length >= 2 && (
              <div className="p-4 text-sm text-stone-500">Aucun résultat.</div>
            )}
        </div>
      )}
    </div>
  );
};

export default IngredientSearch;
